/**
 * ログイン後の復帰先を決める。
 *
 * AuthGate はセッション切れを検知してもリダイレクトせず、アクセスしようとした URL の
 * ままログイン画面を描画する。したがって「ログイン成功時点の URL」= ユーザーが本来
 * 開きたかったページであり、そこへ戻せば元のページに復帰できる。
 * (例: /app/org-admin でセッション切れ → ログイン → /app/org-admin に復帰)
 */

import { currentAppPath, isSafeAppPath } from '../../utils/navigation';

/**
 * 復帰先にすると意味がない / ループになるパス。
 * - /login, /callback: ルート未定義の認証用ダミーパス (公開ホームの導線が使う)
 * - /auth, /onboarding, /accept-invitation, /decline-invitation, /organizations/*:
 *   認証・初回登録フローそのもの。ログイン済みで開き直しても無意味
 */
const NON_RESTORABLE_PREFIXES = [
  '/login',
  '/callback',
  '/auth',
  '/onboarding',
  '/accept-invitation',
  '/decline-invitation',
  '/organizations/create',
  '/organizations/search',
  '/organizations/created',
  '/organizations/join-requested',
  '/organizations/invitation-accepted',
];

/** アプリ内パス (basename 除去済み) から復帰先を決める純粋関数。 */
export function resolvePostLoginTarget(appPathname: string): string {
  const target = appPathname.startsWith('/') ? appPathname : `/${appPathname}`;
  const pathOnly = (target.split(/[?#]/)[0].replace(/\/+$/, '') || '/');
  if (pathOnly === '/') return '/';
  // 外部サイトへ飛ばされる形 (プロトコル相対 URL 等) はホームに落とす
  if (!isSafeAppPath(target)) return '/';
  const blocked = NON_RESTORABLE_PREFIXES.some(
    (prefix) => pathOnly === prefix || pathOnly.startsWith(`${prefix}/`),
  );
  return blocked ? '/' : target;
}

/** 現在の URL からログイン後の復帰先を決める (hardNavigate() にそのまま渡せる)。 */
export function postLoginTarget(): string {
  return resolvePostLoginTarget(currentAppPath());
}
